import { useCallback, useEffect, useState, useRef } from "react";
import {
  GfFigmaTemplateData,
  GfFlyerItemComponentPosition,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

export interface UseBatchEditModalParams {
  open: boolean;
  items: GfFlyerItemComponentPosition[];
  onClose: () => void;
  onUpdated?: () => void;
}

export interface AutocompleteOption<T> {
  label: string;
  value: T;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export function useBatchEditModal({
  open,
  items,
  onClose,
  onUpdated,
}: UseBatchEditModalParams) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<
    AutocompleteOption<GfFigmaTemplateData>[]
  >([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<GfFigmaTemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const validateItems = useCallback(
    (items: GfFlyerItemComponentPosition[]): ValidationResult => {
      if (!items || items.length === 0) {
        return {
          isValid: false,
          error: t(messages.No_Items_Selected_For_Batch_Edit()),
        };
      }

      if (items.length === 1) {
        return {
          isValid: false,
          error: t(messages.Please_Select_More_Than_One_Item_For_Batch_Edit()),
        };
      }

      const storeIds = new Set<string>();

      items.forEach((item) => {
        if (item.store?.id) {
          storeIds.add(item.store.id);
        } else if (
          item.figmaTemplateData?.stores &&
          item.figmaTemplateData.stores.length > 0
        ) {
          item.figmaTemplateData.stores.forEach((store) => {
            if (store.id) {
              storeIds.add(store.id);
            }
          });
        }
      });

      // Check if all items belong to the same store
      if (storeIds.size > 1) {
        return {
          isValid: false,
          error: t(messages.Batch_Edit_Only_Works_For_Items_From_Same_Store()),
        };
      }

      if (storeIds.size === 0) {
        return {
          isValid: false,
          error: t(
            messages.Selected_Items_Do_Not_Have_Valid_Store_Information()
          ),
        };
      }

      return {
        isValid: true,
      };
    },
    [t]
  );

  const handleTemplateSelect = useCallback((values?: GfFigmaTemplateData[]) => {
    const template = values?.[0] || null;
    setSelectedTemplate(template);
  }, []);

  const mapToOption = useCallback(
    (d: GfFigmaTemplateData): AutocompleteOption<GfFigmaTemplateData> => ({
      label: d?.name || d?.id || "Unnamed",
      value: d,
    }),
    []
  );

  const fetchTemplates = useCallback(
    async (q: string) => {
      try {
        setError(null);
        setLoading(true);
        const data =
          await adminApiService.gfFigmaTemplateDataAdminControllerSearch(
            q,
            1,
            20
          );
        const items = data?.results || [];
        setOptions(items.map(mapToOption));
      } catch (err: any) {
        console.error("Failed to search figma template data", err);
        setError(t(messages.Failed_To_Load_Templates_Please_Try_Again()));
      } finally {
        setLoading(false);
      }
    },
    [mapToOption]
  );

  useEffect(() => {
    if (open) {
      setSelectedTemplate(null);
      setError(null);

      const validation = validateItems(items);
      if (!validation.isValid) {
        setValidationError(validation.error || null);
      } else {
        setValidationError(null);
        fetchTemplates("");
      }
    }
  }, [open, items, validateItems, fetchTemplates]);

  const handleSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchTemplates(value || "");
      }, 300);
    },
    [fetchTemplates]
  );

  const handleSave = useCallback(async () => {
    if (!items || items.length === 0) {
      toastError(t(messages.No_Items_Selected_For_Batch_Edit()));
      return;
    }

    if (!selectedTemplate) {
      toastError(t(messages.Please_Select_A_Template()));
      return;
    }

    const validation = validateItems(items);
    if (!validation.isValid) {
      toastError(validation.error || t(messages.Validation_Failed()));
      return;
    }

    try {
      setSaving(true);

      const updatePromises = items.map((item) =>
        adminApiService.updateOneBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
          {
            figmaTemplateData: {
              id: selectedTemplate.id,
            } as GfFigmaTemplateData,
          },
          item.id
        )
      );

      await Promise.all(updatePromises);

      toastSuccess(
        t(messages.Successfully_Updated_Items(), {
          count: items.length,
        })
      );
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error("Failed to batch update component positions", err);
      toastError(t(messages.Failed_To_Save_Some_Or_All_Items()));
    } finally {
      setSaving(false);
    }
  }, [items, selectedTemplate, validateItems, onUpdated, onClose, t]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    options,
    selectedTemplate,
    handleTemplateSelect,
    loading,
    saving,
    error,
    validationError,
    handleSearch,
    handleSave,
    isValid: !validationError,
  } as const;
}
